import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { HiOutlineDocumentPlus, HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmModal from './ui/ConfirmModal';
import EmptyState from './ui/EmptyState';
import PageHeader from './ui/PageHeader';
import TableSkeleton from './ui/TableSkeleton';
import { decryptResponse } from './decrypt';

const sortableColumns = [
  { key: 'title', label: 'Title' },
  { key: 'keyword', label: 'Keyword' },
  { key: 'cat_slug', label: 'Category' },
  { key: 'created_at', label: 'Date' },
];

const BlogList = () => {
  const baseUrl = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const limit = 20;

  const getBlog = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/api/v1/blogs?page=${page}&limit=${limit}`);
      const decryptedData = decryptResponse(
        res.data.data,
        res.data.nonce
    );

      setData(decryptedData || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
    } catch (err) {
      toast.error('Blogs could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBlog();
  }, [baseUrl, page]);

  const sortedData = useMemo(() => {
    const sortedItems = [...data];
    sortedItems.sort((a, b) => {
      const firstValue = a[sortConfig.key] || '';
      const secondValue = b[sortConfig.key] || '';
      const result = String(firstValue).localeCompare(String(secondValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      return sortConfig.direction === 'asc' ? result : -result;
    });

    return sortedItems;
  }, [data, sortConfig]);

  const sortBy = (key) => {
    setSortConfig((currentConfig) => ({
      key,
      direction: currentConfig.key === key && currentConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const deletebtnHandler = async () => {
    if (!pendingDelete) {
      return;
    }

    try {
      setDeleting(true);
      await axios.delete(`${baseUrl}/api/v1/blogs/${pendingDelete.id}`);
      toast.success('Blog deleted successfully.');
      setPendingDelete(null);
      getBlog();
    } catch (err) {
      toast.error('Blog could not be deleted.');
    } finally {
      setDeleting(false);
    }
  };

  const renderSortLabel = (column) => {
    const isActive = sortConfig.key === column.key;
    const direction = isActive && sortConfig.direction === 'asc' ? 'up' : 'down';

    return (
      <button className={`table-sort ${isActive ? 'active' : ''}`} type="button" onClick={() => sortBy(column.key)}>
        {column.label}
        <span aria-hidden="true">{direction === 'up' ? '↑' : '↓'}</span>
      </button>
    );
  };

  return (
    <section className="page-stack">
      <PageHeader
        eyebrow="Blog Management"
        title="Blog List"
        description="Browse, sort, paginate, edit, and remove published blog content."
        actions={
          <Link to="/dashboard/blog" className="btn btn-primary">
            <HiOutlineDocumentPlus aria-hidden="true" />
            Add Blog
          </Link>
        }
      />

      <div className="surface-card table-card">
        <div className="table-responsive">
          <table className="table app-table align-middle">
            <thead>
              <tr>
                <th scope="col">S.No.</th>
                <th scope="col">Image</th>
                {sortableColumns.map((column) => (
                  <th scope="col" key={column.key}>
                    {renderSortLabel(column)}
                  </th>
                ))}
                <th scope="col">Meta Desc.</th>
                <th scope="col">Slug</th>
                <th scope="col" className="text-end">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton columns={9} rows={6} />
              ) : sortedData.length > 0 ? (
                sortedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>
                      <img src={item.mobile_image} alt={item.title || 'Blog'} className="table-image" />
                    </td>
                    <td className="fw-semibold table-title-cell">{item.title}</td>
                    <td>{item.keyword}</td>
                    <td>
                      <span className="slug-pill">{item.cat_slug}</span>
                    </td>
                    <td>{item.created_at}</td>
                    <td className="table-description-cell">{item.meta_description}</td>
                    <td>
                      <span className="slug-pill neutral">{item.slug}</span>
                    </td>
                    <td>
                      <div className="table-actions justify-content-end">
                        <Link className="icon-button success" to={`/dashboard/edit-blog/${item.id}`} aria-label={`Edit ${item.title}`}>
                          <HiOutlinePencilSquare aria-hidden="true" />
                        </Link>
                        <button className="icon-button danger" type="button" aria-label={`Delete ${item.title}`} onClick={() => setPendingDelete(item)}>
                          <HiOutlineTrash aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>
                    <EmptyState
                      title="No blogs found"
                      description="Create a new blog post to start filling the publishing queue."
                      action={
                        <Link to="/dashboard/blog" className="btn btn-primary">
                          Add Blog
                        </Link>
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-bar">
          <button className="btn btn-soft-secondary" type="button" disabled={page === 1 || loading} onClick={() => setPage((currentPage) => currentPage - 1)}>
            Previous
          </button>
          <span>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            className="btn btn-soft-secondary"
            type="button"
            disabled={page === totalPages || loading}
            onClick={() => setPage((currentPage) => currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <ConfirmModal
        show={Boolean(pendingDelete)}
        title="Delete blog?"
        description={`This will permanently remove ${pendingDelete?.title || 'this blog post'}.`}
        confirmLabel="Delete Blog"
        isBusy={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={deletebtnHandler}
      />
    </section>
  );
};

export default BlogList;
